import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, Lock } from "lucide-react";
import { useSite, resolveImageUrl } from "../context/SiteContext";

export default function Footer() {
  const { site } = useSite();
  const brand = site?.brand_name || "Let Go Wellness";
  const wordmark = `${brand}.`;
  const qr = site?.qr_code_url;

  return (
    <footer className="relative bg-[#2C4C3B] text-[#FDFBF7] pt-24 md:pt-32 pb-12 overflow-hidden" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-12 mb-20">
          <div className="md:col-span-6">
            <p className="overline text-[#EAE5D9] mb-6 opacity-70">{brand}</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-2xl">
              You don't have to carry it alone. <em className="italic text-[#EAE5D9]">Begin here.</em>
            </h2>
            <a
              href="#enroll"
              data-testid="footer-cta-enroll"
              className="inline-flex items-center gap-2 mt-10 px-8 py-4 bg-[#FDFBF7] text-[#2C4C3B] hover:bg-[#EAE5D9] transition-colors text-sm tracking-wide"
            >
              Enroll today <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </a>

            {/* Trust badges */}
            <div className="flex flex-wrap items-center gap-6 mt-12">
              <span data-testid="footer-hipaa-badge" className="inline-flex items-center gap-2 px-3 py-2 border border-[#FDFBF7]/30 text-xs uppercase tracking-[0.2em] text-[#EAE5D9]">
                <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.5} /> HIPAA compliant
              </span>
              <span className="inline-flex items-center gap-2 px-3 py-2 border border-[#FDFBF7]/30 text-xs uppercase tracking-[0.2em] text-[#EAE5D9]">
                <Lock className="w-3.5 h-3.5" strokeWidth={1.5} /> 256-bit encrypted
              </span>
            </div>
          </div>

          <div className="md:col-span-2 md:col-start-8">
            <p className="overline text-[#EAE5D9] opacity-70 mb-5">Visit</p>
            <p className="text-sm leading-relaxed text-[#EAE5D9]">
              {site?.address_line1}<br />{site?.address_line2}
            </p>
          </div>
          <div className="md:col-span-2">
            <p className="overline text-[#EAE5D9] opacity-70 mb-5">Contact</p>
            <p className="text-sm leading-relaxed text-[#EAE5D9]">
              <span className="opacity-60 text-xs tracking-[0.22em] uppercase mr-2">Office</span>{site?.phone}
              {site?.phone2 ? <><br /><span className="opacity-60 text-xs tracking-[0.22em] uppercase mr-2">Cell</span>{site.phone2}</> : null}
              <br />{site?.email}
            </p>
          </div>
          <div className="md:col-span-2">
            <p className="overline text-[#EAE5D9] opacity-70 mb-5">Sections</p>
            <ul className="space-y-2 text-sm text-[#EAE5D9]">
              <li><a href="/#services" className="hover:text-white">Services</a></li>
              <li><a href="/#approach" className="hover:text-white">Approach</a></li>
              <li><a href="/#team" className="hover:text-white">Therapists</a></li>
              <li><a href="/#book" className="hover:text-white">Book online</a></li>
              <li><Link to="/spa" className="hover:text-white">Spa</Link></li>
              <li><Link to="/share" data-testid="footer-share-link" className="hover:text-white">Share / QR code</Link></li>
              <li><Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-white">Privacy & HIPAA</Link></li>
            </ul>
          </div>

          {qr && (
            <div className="md:col-span-2 md:col-start-11 hidden md:block">
              <p className="overline text-[#EAE5D9] opacity-70 mb-5">Scan</p>
              <Link to="/share" className="block bg-[#FDFBF7] p-3 w-full max-w-[140px] hover:opacity-90 transition-opacity" data-testid="footer-qr">
                <img src={resolveImageUrl(qr)} alt="Scan to visit letgowelllness.com" className="w-full aspect-square object-contain" />
              </Link>
              <p className="text-xs text-[#EAE5D9] opacity-70 mt-3">
                Point your camera here.
              </p>
            </div>
          )}
        </div>

        <div className="overflow-hidden">
          <div className="font-serif italic text-[14vw] sm:text-[13vw] md:text-[12vw] leading-[0.85] text-[#FDFBF7]/95 whitespace-nowrap tracking-tight select-none lowercase" data-testid="footer-wordmark">
            {wordmark}
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pt-10 border-t border-[#FDFBF7]/15 mt-6 text-xs text-[#EAE5D9] opacity-80">
          <p>© {new Date().getFullYear()} {brand}, PLLC. All rights reserved.</p>
          <p>If you are in crisis, please call <a href="tel:988" className="underline">988</a> or go to your nearest emergency room.</p>
        </div>
      </div>
    </footer>
  );
}
